/**
 * Platform Commits Page - EA-Agile
 * Displays audit trail of all platform commits (pending, approved, rejected, executed)
 *
 * Features:
 * - Table listing commits with status badges
 * - Filtering by status, repository, agent
 * - Pagination support
 * - Expandable commit details
 * - GitHub commit links when available
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  FiGitCommit,
  FiRefreshCw,
  FiFilter,
  FiChevronDown,
  FiChevronUp,
  FiExternalLink,
  FiFileText,
  FiUser,
  FiCalendar,
  FiCpu
} from 'react-icons/fi';
import { Make_Authorized_API_Call, isSuccessResponse } from '../../api/apiClient';
import { API_ENDPOINTS } from '../../services/api/endpoints';
import { useStatus } from '../../contexts/StatusProvider';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

type CommitStatus = 'pending' | 'approved' | 'rejected' | 'committed' | 'failed';
type StatusFilter = 'all' | CommitStatus;

interface FileChange {
  path: string;
  additions: number;
  deletions: number;
  status: 'added' | 'modified' | 'deleted';
}

interface PlatformCommit {
  commit_id: string;
  Client_ID: string;
  repository: string;
  branch: string;
  agent_name: string;
  status: CommitStatus;
  commit_sha?: string;
  commit_message: string;
  original_request?: string;
  files_changed?: FileChange[];
  approved_by?: string;
  approved_at?: string;
  rejected_by?: string;
  rejected_at?: string;
  rejection_reason?: string;
  created_at: string;
  updated_at: string;
  github_url?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

const PlatformCommitsPage: React.FC = () => {
  const { Client_ID } = useStatus();

  // State management
  const [commits, setCommits] = useState<PlatformCommit[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [repositoryFilter, setRepositoryFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');

  // Pagination state
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 50;

  // Expanded rows state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Get effective client ID (from StatusProvider or localStorage for super admin context)
  const effectiveClientId = Client_ID || localStorage.getItem('selectedClientId');

  // Get unique repositories and agents for filter dropdowns
  const uniqueRepositories = [...new Set(commits.map(c => c.repository))];
  const uniqueAgents = [...new Set(commits.map(c => c.agent_name))];

  // =============================================================================
  // API CALLS
  // =============================================================================

  const fetchCommits = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      // Build query params
      const params: Record<string, string> = {
        limit: limit.toString(),
        offset: offset.toString()
      };

      if (effectiveClientId) {
        params.Client_ID = effectiveClientId;
      }

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await Make_Authorized_API_Call<PlatformCommit>(
        API_ENDPOINTS.PLATFORM_COMMITS.LIST,
        'GET',
        undefined,
        { params }
      );

      if (!isSuccessResponse(response)) {
        throw new Error(response.message || 'Failed to fetch platform commits');
      }

      setCommits(response.data.Records || []);
      setTotalCount(response.meta.Total_Records || response.data.Records?.length || 0);
    } catch (err) {
      console.error('Error fetching platform commits:', err);
      setError(err instanceof Error ? err.message : 'Failed to load platform commits');
    } finally {
      setIsLoading(false);
    }
  }, [effectiveClientId, statusFilter, offset, limit]);

  // Initial fetch and on refresh trigger
  useEffect(() => {
    fetchCommits();
  }, [fetchCommits, refreshTrigger]);

  // =============================================================================
  // FILTER & DISPLAY HELPERS
  // =============================================================================

  // Filter commits based on selected filters (client-side filtering for repo/agent)
  const filteredCommits = commits.filter((commit) => {
    const matchesRepository = !repositoryFilter || commit.repository === repositoryFilter;
    const matchesAgent = !agentFilter || commit.agent_name === agentFilter;
    return matchesRepository && matchesAgent;
  });

  // Get status badge color and styling
  const getStatusBadge = (status: CommitStatus): { bg: string; text: string; label: string } => {
    switch (status) {
      case 'pending':
        return { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' };
      case 'approved':
        return { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Approved' };
      case 'rejected':
        return { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' };
      case 'committed':
        return { bg: 'bg-green-100', text: 'text-green-800', label: 'Committed' };
      case 'failed':
        return { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' };
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
    }
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Truncate SHA for display
  const truncateSha = (sha: string): string => {
    return sha.substring(0, 7);
  };

  // Toggle row expansion
  const toggleRowExpansion = (commitId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commitId)) {
        newSet.delete(commitId);
      } else {
        newSet.add(commitId);
      }
      return newSet;
    });
  };

  // Handle refresh
  const handleRefresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Handle pagination
  const handlePreviousPage = () => {
    setOffset(Math.max(0, offset - limit));
  };

  const handleNextPage = () => {
    if (offset + limit < totalCount) {
      setOffset(offset + limit);
    }
  };

  // =============================================================================
  // RENDER: LOADING STATE
  // =============================================================================

  if (isLoading && commits.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          <FiGitCommit className="h-8 w-8 text-indigo-600" />
          <h1 className="text-3xl font-bold text-gray-900">Platform Commits</h1>
        </div>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading platform commits...</p>
          </div>
        </div>
      </div>
    );
  }

  // =============================================================================
  // RENDER: ERROR STATE
  // =============================================================================

  if (error && commits.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          <FiGitCommit className="h-8 w-8 text-indigo-600" />
          <h1 className="text-3xl font-bold text-gray-900">Platform Commits</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <FiRefreshCw className="h-4 w-4" />
            <span>Retry</span>
          </button>
        </div>
      </div>
    );
  }

  // =============================================================================
  // RENDER: MAIN CONTENT
  // =============================================================================

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <FiGitCommit className="h-8 w-8 text-indigo-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Platform Commits</h1>
              <p className="text-gray-600 mt-1">
                Audit trail of all platform repository commits
              </p>
            </div>
          </div>

          {/* Refresh button */}
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Refresh commits list"
          >
            <FiRefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600">Total Commits</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{totalCount}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600">Pending</div>
            <div className="text-2xl font-bold text-yellow-600 mt-1">
              {commits.filter(c => c.status === 'pending').length}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600">Approved</div>
            <div className="text-2xl font-bold text-blue-600 mt-1">
              {commits.filter(c => c.status === 'approved').length}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600">Committed</div>
            <div className="text-2xl font-bold text-green-600 mt-1">
              {commits.filter(c => c.status === 'committed').length}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-600">Rejected/Failed</div>
            <div className="text-2xl font-bold text-red-600 mt-1">
              {commits.filter(c => c.status === 'rejected' || c.status === 'failed').length}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex items-center space-x-3 mb-4">
          <FiFilter className="h-5 w-5 text-gray-600" />
          <h3 className="text-sm font-semibold text-gray-900">Filters</h3>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {/* Status filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as StatusFilter);
                setOffset(0); // Reset pagination on filter change
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="committed">Committed</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          {/* Repository filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Repository
            </label>
            <select
              value={repositoryFilter}
              onChange={(e) => setRepositoryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="">All Repositories</option>
              {uniqueRepositories.map(repo => (
                <option key={repo} value={repo}>{repo}</option>
              ))}
            </select>
          </div>

          {/* Agent filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Agent
            </label>
            <select
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="">All Agents</option>
              {uniqueAgents.map(agent => (
                <option key={agent} value={agent}>{agent}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Commits Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {filteredCommits.length === 0 ? (
          <div className="p-12 text-center">
            <FiGitCommit className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No commits found</h3>
            <p className="text-gray-600">
              {commits.length === 0
                ? 'No platform commits recorded yet.'
                : 'No commits match the selected filters.'}
            </p>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Repository
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Branch
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Agent
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Approved By
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Commit SHA
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <span className="sr-only">Expand</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredCommits.map((commit) => {
                    const isExpanded = expandedRows.has(commit.commit_id);
                    const statusBadge = getStatusBadge(commit.status);

                    return (
                      <React.Fragment key={commit.commit_id}>
                        {/* Main Row */}
                        <tr
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => toggleRowExpansion(commit.commit_id)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center space-x-2">
                              <FiCalendar className="h-4 w-4 text-gray-400" />
                              <span>{formatDate(commit.created_at)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {commit.repository}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            <code className="px-2 py-1 bg-gray-100 rounded text-xs">
                              {commit.branch}
                            </code>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            <div className="flex items-center space-x-2">
                              <FiCpu className="h-4 w-4 text-gray-400" />
                              <span>{commit.agent_name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                              {statusBadge.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {commit.approved_by ? (
                              <div className="flex items-center space-x-2">
                                <FiUser className="h-4 w-4 text-gray-400" />
                                <span>{commit.approved_by}</span>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {commit.commit_sha ? (
                              commit.github_url ? (
                                <a
                                  href={commit.github_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center space-x-1 text-indigo-600 hover:text-indigo-800"
                                  onClick={(e) => e.stopPropagation()}
                                  title="View commit on GitHub"
                                >
                                  <code className="text-xs">{truncateSha(commit.commit_sha)}</code>
                                  <FiExternalLink className="h-3 w-3" />
                                </a>
                              ) : (
                                <code className="text-xs text-gray-600">{truncateSha(commit.commit_sha)}</code>
                              )
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            <button
                              className="text-gray-400 hover:text-gray-600"
                              aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                            >
                              {isExpanded ? (
                                <FiChevronUp className="h-5 w-5" />
                              ) : (
                                <FiChevronDown className="h-5 w-5" />
                              )}
                            </button>
                          </td>
                        </tr>

                        {/* Expanded Details Row */}
                        {isExpanded && (
                          <tr className="bg-gray-50">
                            <td colSpan={8} className="px-6 py-4">
                              <div className="space-y-4">
                                {/* Commit Message */}
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center space-x-2">
                                    <FiGitCommit className="h-4 w-4" />
                                    <span>Commit Message</span>
                                  </h4>
                                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                                    <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                                      {commit.commit_message}
                                    </pre>
                                  </div>
                                </div>

                                {/* Original Request */}
                                {commit.original_request && (
                                  <div>
                                    <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center space-x-2">
                                      <FiFileText className="h-4 w-4" />
                                      <span>Original Request</span>
                                    </h4>
                                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                        {commit.original_request}
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {/* Files Changed */}
                                {commit.files_changed && commit.files_changed.length > 0 && (
                                  <div>
                                    <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center space-x-2">
                                      <FiFileText className="h-4 w-4" />
                                      <span>Files Changed ({commit.files_changed.length})</span>
                                    </h4>
                                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                      <ul className="divide-y divide-gray-200">
                                        {commit.files_changed.map((file, index) => (
                                          <li key={index} className="px-3 py-2 flex items-center justify-between">
                                            <span className="text-sm font-mono text-gray-700">
                                              {file.path}
                                            </span>
                                            <div className="flex items-center space-x-2 text-xs">
                                              {file.additions > 0 && (
                                                <span className="text-green-600">+{file.additions}</span>
                                              )}
                                              {file.deletions > 0 && (
                                                <span className="text-red-600">-{file.deletions}</span>
                                              )}
                                              <span className={`px-1.5 py-0.5 rounded text-xs ${
                                                file.status === 'added' ? 'bg-green-100 text-green-700' :
                                                file.status === 'deleted' ? 'bg-red-100 text-red-700' :
                                                'bg-yellow-100 text-yellow-700'
                                              }`}>
                                                {file.status}
                                              </span>
                                            </div>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  </div>
                                )}

                                {/* Rejection Reason */}
                                {commit.status === 'rejected' && commit.rejection_reason && (
                                  <div>
                                    <h4 className="text-sm font-medium text-red-900 mb-2">
                                      Rejection Reason
                                    </h4>
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                      <p className="text-sm text-red-700">
                                        {commit.rejection_reason}
                                      </p>
                                      {commit.rejected_by && (
                                        <p className="text-xs text-red-600 mt-2">
                                          Rejected by {commit.rejected_by} on {commit.rejected_at ? formatDate(commit.rejected_at) : 'N/A'}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Approval Details */}
                                {commit.approved_by && commit.approved_at && (
                                  <div className="text-xs text-gray-500">
                                    Approved by {commit.approved_by} on {formatDate(commit.approved_at)}
                                  </div>
                                )}

                                {/* Metadata */}
                                <div className="flex items-center justify-between pt-2 border-t border-gray-200 text-xs text-gray-500">
                                  <span>Commit ID: {commit.commit_id}</span>
                                  <span>Last updated: {formatDate(commit.updated_at)}</span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{offset + 1}</span> to{' '}
                <span className="font-medium">{Math.min(offset + limit, totalCount)}</span> of{' '}
                <span className="font-medium">{totalCount}</span> commits
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handlePreviousPage}
                  disabled={offset === 0}
                  className="px-3 py-1 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={offset + limit >= totalCount}
                  className="px-3 py-1 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Status Legend */}
      <div className="mt-8 bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Status Legend</h3>
        <div className="grid grid-cols-5 gap-4 text-xs">
          <div className="flex items-center space-x-2">
            <span className="inline-block w-3 h-3 rounded-full bg-yellow-500"></span>
            <span className="text-gray-600">Pending - Awaiting approval</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="inline-block w-3 h-3 rounded-full bg-blue-500"></span>
            <span className="text-gray-600">Approved - Ready to commit</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="inline-block w-3 h-3 rounded-full bg-red-500"></span>
            <span className="text-gray-600">Rejected - Not approved</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="inline-block w-3 h-3 rounded-full bg-green-500"></span>
            <span className="text-gray-600">Committed - Successfully pushed</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="inline-block w-3 h-3 rounded-full bg-red-800"></span>
            <span className="text-gray-600">Failed - Commit operation failed</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformCommitsPage;
